import { dashboardEnv } from '@cargoflow/env/dashboard'
import { createAuthClient } from 'better-auth/react'

/**
 * Better Auth React client
 * Configured to communicate with the API's Better Auth endpoints
 *
 * Uses httpOnly cookies for session management (more secure than localStorage)
 * - Session cookies are set by the server
 * - JavaScript cannot access the session token (XSS-safe)
 * - CSRF protection via SameSite cookies
 */
export const authClient = createAuthClient({
  baseURL: dashboardEnv.CARGO_API_URL,
  sessionOptions: {
    // Don't poll for session updates (rely on explicit refetch)
    refetchInterval: 0,
    // Only refetch on window focus, not continuously
    refetchOnWindowFocus: true,
    // Don't refetch when offline
    refetchWhenOffline: false,
  },
})

// Export typed hooks and utilities
export const { signIn, signUp, signOut, useSession, requestPasswordReset, resetPassword } =
  authClient

// Session cache for deduplication
// Prevents multiple simultaneous getSession calls from making duplicate requests
type SessionCache = {
  promise: ReturnType<typeof authClient.getSession> | null
  timestamp: number
}

const sessionCache: SessionCache = {
  promise: null,
  timestamp: 0,
}

// Cache TTL in ms - reuse cached session for 2 seconds
const SESSION_CACHE_TTL = 2000

/**
 * Cached getSession that deduplicates simultaneous requests
 * Multiple calls within SESSION_CACHE_TTL share the same promise
 */
export async function getSession() {
  const now = Date.now()

  // If we have a recent cached promise, return it
  if (sessionCache.promise && now - sessionCache.timestamp < SESSION_CACHE_TTL) {
    return sessionCache.promise
  }

  // Create new request and cache it
  sessionCache.timestamp = now
  sessionCache.promise = authClient.getSession()

  return sessionCache.promise
}
